import React, { Fragment, useEffect, useMemo, useState, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import { compose } from 'recompose';
import { Typography } from '@material-ui/core';
import { Alert } from 'Components';
import { changePasswordPublicGateway } from 'Services/changePasswordPublicGateway';
import { browserHistory } from 'Helper/history';
import { PublicRouter } from 'Navigation/Router';
import { RecaptchaLayout } from 'Containers/RecaptchaLayout';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PasswordChangeForm from 'Components/PasswordChangeForm';
import { validator } from 'Helper/validate';
import { usePasswordPolicy } from 'Helper/usePasswordPolicy';
import { handlePasswordChangeApiError } from 'Helper/passwordChangeErrorHandler';

const isStateValid = (state) => {
  if (!state) return false;
  const { fromLogin, username, currentPassword } = state;
  return !!(fromLogin && username && currentPassword);
};

const ChangeDefaultPassword = ({ t, challenge, client }) => {
  const locationState = browserHistory.location.state;

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [newPasswordSameAsCurrentError, setNewPasswordSameAsCurrentError] = useState(false);
  const [newPasswordBackendError, setNewPasswordBackendError] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  const recaptchaEnabled = Boolean(client?.recaptchaConfiguration && client.recaptchaConfiguration.enabled);
  const clientId = client?.clientId || '';
  const { passwordPolicy, isLoadingPasswordPolicy } = usePasswordPolicy(client.tenantId);

  const { buttonColor } = useMemo(() => {
    const { buttonColor: themeBtn } = client.theme || {};
    return {
      buttonColor: validator.isValidColor(themeBtn) ? themeBtn : undefined,
    };
  }, [client]);

  const fallbackToLogin = useCallback(() => {
    Alert.error(t('Please login again'));
    browserHistory.replace({
      pathname: PublicRouter.Login.url,
      search: challenge ? `challenge=${challenge}` : '',
    });
  }, [t, challenge]);

  useEffect(() => {
    if (!isStateValid(locationState)) {
      fallbackToLogin();
      return;
    }
  }, [locationState, fallbackToLogin]);

  const { username, currentPassword } = locationState || {};

  const handleSubmit = useCallback(async () => {
    if (!isStateValid(locationState)) {
      fallbackToLogin();
      return;
    }

    const validCurrentPassword = !validator.isEmpty(currentPassword);
    const validNew = validator.isValidPassword(newPassword, passwordPolicy);
    const isSameAsCurrentPassword = validCurrentPassword && currentPassword === newPassword;
    const match = newPassword === confirmPassword;

    setCurrentPasswordError(!validCurrentPassword);
    setNewPasswordError(!validNew || isSameAsCurrentPassword);
    setNewPasswordSameAsCurrentError(isSameAsCurrentPassword);
    setConfirmPasswordError(!match);
    setNewPasswordBackendError('');
    if (!validCurrentPassword || !validNew || !match || isSameAsCurrentPassword) return;

    if (!clientId) {
      Alert.error(t('Invalid client'));
      return;
    }

    if (recaptchaEnabled && !executeRecaptcha) {
      Alert.error(t('Unable to validate reCAPTCHA. Please try again.'));
      return;
    }

    setIsLoading(true);
    try {
      const request = {
        username,
        client_id: clientId,
        current_password: currentPassword,
        new_password: newPassword,
      };

      if (recaptchaEnabled) {
        request.recaptcha_token = await executeRecaptcha('PASSWORD_CHANGE');
      }

      await changePasswordPublicGateway(request);
      Alert.success(t('Update Password successfully'));
      browserHistory.replace({
        pathname: PublicRouter.Login.url,
        search: `challenge=${challenge}`,
      });
    } catch (error) {
      handlePasswordChangeApiError({ error, t, setUsernameError, setNewPasswordError, setNewPasswordBackendError });
    } finally {
      setIsLoading(false);
    }
  }, [
    locationState,
    newPassword,
    confirmPassword,
    passwordPolicy,
    currentPassword,
    username,
    clientId,
    recaptchaEnabled,
    executeRecaptcha,
    t,
    challenge,
    fallbackToLogin,
  ]);

  return (
    <Fragment>
      <Typography variant='h5' className='mb-12'>
        {t('Change default password')}
      </Typography>
      <Typography variant='body2' className='mb-24 color-gray'>
        {t('You are required to update your default password before continuing')}
      </Typography>

      <PasswordChangeForm
        t={t}
        username={username || ''}
        currentPassword={currentPassword || ''}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
        showPassword={showPassword}
        usernameError={usernameError}
        currentPasswordError={currentPasswordError}
        newPasswordError={newPasswordError}
        confirmPasswordError={confirmPasswordError}
        newPasswordSameAsCurrentError={newPasswordSameAsCurrentError}
        newPasswordBackendError={newPasswordBackendError}
        passwordPolicy={passwordPolicy}
        isLoadingPasswordPolicy={isLoadingPasswordPolicy}
        isLoading={isLoading}
        buttonColor={buttonColor}
        usernameReadOnly
        currentPasswordReadOnly
        onUsernameChange={(value) => {
          setUsernameError(!validator.isValidUsername(value));
        }}
        onCurrentPasswordChange={(value) => {
          setCurrentPasswordError(validator.isEmpty(value));
          if (newPassword) {
            const isSameAsCurrentPassword = newPassword === value;
            setNewPasswordError(!validator.isValidPassword(newPassword, passwordPolicy) || isSameAsCurrentPassword);
            setNewPasswordSameAsCurrentError(isSameAsCurrentPassword);
          }
        }}
        onNewPasswordChange={(value) => {
          const isSameAsCurrentPassword = value === currentPassword;
          setNewPassword(value);
          setNewPasswordError(!validator.isValidPassword(value, passwordPolicy) || isSameAsCurrentPassword);
          setNewPasswordSameAsCurrentError(isSameAsCurrentPassword);
          setNewPasswordBackendError('');
          if (confirmPassword) setConfirmPasswordError(value !== confirmPassword);
        }}
        onConfirmPasswordChange={(value) => {
          setConfirmPassword(value);
          setConfirmPasswordError(newPassword !== value);
        }}
        onToggleShowPassword={() => setShowPassword((v) => !v)}
        onSubmit={handleSubmit}
        showLoginLink={false}
      />
    </Fragment>
  );
};

export default compose(
  withTranslation(),
  RecaptchaLayout(),
)(ChangeDefaultPassword);
